import { useState, useMemo, useRef, useEffect } from "react";
import { usePersistedState } from "../../hooks/usePersistedState";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Workout } from "../../libs/workout/types";
import { useGetSpecialCategoriesQuery } from "../../libs/specialCategory/api";
import { useGetAllExercisesQuery } from "../../libs/exercises/api";
import { useGetUserQuery } from "../../libs/user/api";
import { BodyMuscleMap } from "./BodyMuscleMap";
import { CHECK_DISTRICTS, CHECK_DISTRICT_NORMALIZE, DISTRICT_TO_SLUGS } from "./muscleConfig";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

type RecoveryChartProps = {
  workout: Workout;
  weeksColumns: string[];
  userId?: number;
};

type CheckExerciseData = {
  name: string;
  district: string;
  exerciseIndex: number;
  dayIndex: number;
};

// Plugin per disegnare le bande colorate di sfondo
const bandsPlugin = {
  id: "recoveryBands",
  beforeDraw(chart: any) {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea) return;
    const yScale = scales.y;

    const bands = [
      { from: 2, to: 3, color: "rgba(239, 68, 68, 0.25)" },
      { from: 3, to: 5, color: "rgba(234, 179, 8, 0.2)" },
      { from: 5, to: 6, color: "rgba(34, 197, 94, 0.2)" },
    ];

    ctx.save();
    for (const band of bands) {
      const yTop = yScale.getPixelForValue(band.to);
      const yBottom = yScale.getPixelForValue(band.from);
      ctx.fillStyle = band.color;
      ctx.fillRect(chartArea.left, yTop, chartArea.width, yBottom - yTop);
    }
    ctx.restore();
  },
};

export const RecoveryChart: React.FC<RecoveryChartProps> = ({
  workout,
  weeksColumns,
  userId,
}) => {
  const { data: categories } = useGetSpecialCategoriesQuery();
  const { data: allExercises } = useGetAllExercisesQuery();
  const { data: user } = useGetUserQuery(userId + "", { skip: !userId });
  const gender = user?.gender === "F" ? "female" as const : "male" as const;

  const [selectedDistricts, setSelectedDistricts] = usePersistedState<string[]>(`fitshape:chart:${userId}:recoveryDistricts`, []);

  // Trova tutti gli esercizi CHECK nel workout e il loro checkDistrict
  const checkExercises = useMemo(() => {
    if (!workout.data || !categories || !allExercises) return [];
    const checkCat = categories.find((c) =>
      c.name.toUpperCase().includes("CHECK")
    );
    if (!checkCat) return [];

    const found: CheckExerciseData[] = [];
    const seen = new Set<string>();

    workout.data[0]?.days?.forEach((day, dayIndex) => {
      day.exercises?.forEach((ex, exerciseIndex) => {
        const catId = ex.exercise?.specialCategoryId;
        if (catId !== checkCat.id) return;
        const key = `${dayIndex}-${exerciseIndex}`;
        if (seen.has(key)) return;
        seen.add(key);

        // Cerca il checkDistrict dall'esercizio nella libreria
        const libEx = allExercises.find(
          (e) => String(e.id) === String(ex.exercise.id)
        );
        const district =
          ex.exercise.checkDistrict ||
          libEx?.checkDistrict ||
          "";

        if (district) {
          found.push({
            name: ex.exercise.name,
            district,
            exerciseIndex,
            dayIndex,
          });
        }
      });
    });

    return found;
  }, [workout, categories, allExercises]);

  // Distretti disponibili (quelli che hanno almeno un check exercise)
  const availableDistricts = useMemo(() => {
    const districts = new Set(checkExercises.map((e) => e.district));
    return CHECK_DISTRICTS.filter((d) => districts.has(d));
  }, [checkExercises]);

  // Toggle distretto dal body SVG o dal menu
  const handleToggleDistrict = (district: string) => {
    setSelectedDistricts((prev) =>
      prev.includes(district)
        ? prev.filter((d) => d !== district)
        : [...prev, district]
    );
  };

  // Calcola i dataset per i distretti selezionati
  const datasets = useMemo(() => {
    if (!workout.data || selectedDistricts.length === 0) return [];

    const ds: any[] = [];

    selectedDistricts.forEach((district) => {
      const exercises = checkExercises.filter((e) => e.district === district);
      if (exercises.length === 0) return;

      // Aggrega i dati di tutti gli esercizi check per questo distretto
      exercises.forEach((info) => {
        const danno: (number | null)[] = [];
        const fatica: (number | null)[] = [];

        workout.data.forEach((week) => {
          const day = week.days[info.dayIndex];
          const ex = day?.exercises[info.exerciseIndex]?.exercise;

          if (!ex || !ex.completed || !ex.sets || ex.sets.length < 4) {
            danno.push(null);
            fatica.push(null);
            return;
          }

          const getScore = (set: any) => {
            const val = set.repsDone != null ? set.repsDone : set.reps;
            return typeof val === "number" ? val : 0;
          };

          danno.push(getScore(ex.sets[0]) + getScore(ex.sets[1]));
          fatica.push(getScore(ex.sets[2]) + getScore(ex.sets[3]));
        });

        const suffix = exercises.length > 1 ? ` (${info.name})` : "";

        ds.push({
          label: `Danno ${district}${suffix}`,
          data: danno,
          borderColor: "#f87171",
          backgroundColor: "#f87171",
          pointBackgroundColor: "#f87171",
          pointBorderColor: "#f87171",
          pointRadius: 5,
          pointHoverRadius: 7,
          borderWidth: 2.5,
          borderDash: [],
          fill: false,
          tension: 0.3,
          spanGaps: true,
        });

        ds.push({
          label: `Fatica ${district}${suffix}`,
          data: fatica,
          borderColor: "#60a5fa",
          backgroundColor: "#60a5fa",
          pointBackgroundColor: "#60a5fa",
          pointBorderColor: "#60a5fa",
          pointRadius: 5,
          pointHoverRadius: 7,
          borderWidth: 2.5,
          borderDash: [6, 3],
          fill: false,
          tension: 0.3,
          spanGaps: true,
        });
      });
    });

    return ds;
  }, [workout, selectedDistricts, checkExercises]);

  if (checkExercises.length === 0) return null;

  const chartData = {
    labels: weeksColumns,
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "nearest" as const,
      intersect: true,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (item: any) => {
            const val = item.raw;
            if (val === null) return `${item.dataset.label}: N/A`;
            let zone = "Critico";
            if (val >= 5) zone = "Ok";
            else if (val >= 3) zone = "Attenzione";
            return `${item.dataset.label}: ${val}/6 — ${zone}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: true, color: "#374151" },
        ticks: { color: "#9ca3af", font: { size: 11 } },
        border: { color: "#374151" },
      },
      y: {
        min: 2,
        max: 6,
        grid: { display: true, color: "#374151" },
        ticks: {
          color: "#9ca3af",
          font: { size: 11 },
          stepSize: 1,
          callback: (value: any) => {
            if (value === 2) return "2 (min)";
            if (value === 6) return "6 (max)";
            return value;
          },
        },
        border: { color: "#374151" },
      },
    },
  };

  // Filtra i distretti disponibili per l'SVG body
  // Mappo solo quelli che hanno un check exercise E sono selezionati
  const bodyActiveDistricts = selectedDistricts.filter(
    (d) => DISTRICT_TO_SLUGS[d]
  );

  return (
    <div className="bg-gray-800 dark:bg-gray-800 dark:border dark:border-gray-700 rounded-xl p-6 mt-6">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-100">Danno Recupero</h3>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-center">
        {/* Colonna sinistra: dropdown + corpo SVG (identica a TonnageSection) */}
        <div className="w-full lg:w-[25%] flex flex-col items-center gap-4 lg:sticky lg:top-36 lg:self-center">
          {/* Dropdown distretti */}
          <div className="flex items-center gap-2 w-full max-w-xs">
            <div className="flex-1 min-w-0">
              <RecoveryDistrictDropdown
                availableDistricts={availableDistricts}
                selectedDistricts={selectedDistricts}
                onSelectionChange={setSelectedDistricts}
              />
            </div>
            {selectedDistricts.length > 0 && (
              <button
                onClick={() => setSelectedDistricts([])}
                className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-700 border border-gray-600 text-gray-400 hover:bg-gray-600 hover:text-gray-200 transition-colors flex items-center justify-center"
                title="Reset selezione"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          <BodyMuscleMap
            activeDistricts={bodyActiveDistricts}
            dimDistricts={availableDistricts.filter((d) => !selectedDistricts.includes(d))}
            hoveredDistrict={null}
            onHoverDistrict={() => {}}
            onClickDistrict={(d: string) => {
              const normalized = CHECK_DISTRICT_NORMALIZE[d] || d;
              if (availableDistricts.includes(normalized)) {
                handleToggleDistrict(normalized);
              }
            }}
            gender={gender}
            scale={0.7}
            uniformColor="#EAB308"
          />
        </div>

        {/* Colonna destra: grafico + legenda */}
        <div className="w-full lg:w-[75%] min-w-0 flex flex-col gap-2">
          <div className="flex gap-6 items-start">
            <div className="flex-1 min-w-0" style={{ height: 350 }}>
              <Line data={chartData} options={options} plugins={[bandsPlugin]} />
            </div>

            {/* Legenda */}
            <div className="flex flex-col gap-1 justify-center flex-shrink-0 pl-4 border-l border-gray-700">
              {/* Tipo linea */}
              <span className="text-[10px] text-gray-500 uppercase tracking-wider">Tipo linea</span>
              <div className="flex items-center gap-1.5 text-[10px] text-gray-300">
                <span className="w-4 h-0 border-t-2 border-red-400 inline-block" />
                Danno (Q1+Q2)
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-gray-300">
                <span className="w-4 h-0 border-t-2 border-dashed border-blue-400 inline-block" />
                Fatica (Q3+Q4)
              </div>

              {/* Fasce */}
              <div className="flex flex-col gap-1 mt-3 pt-3 border-t border-gray-700">
                <span className="text-[10px] text-gray-500 uppercase tracking-wider">Fasce</span>
                <div className="flex items-center gap-1.5 text-[10px] text-gray-300">
                  <span className="w-2.5 h-2.5 rounded-sm bg-green-500/40 inline-block" />
                  5-6 Ok
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-gray-300">
                  <span className="w-2.5 h-2.5 rounded-sm bg-yellow-500/40 inline-block" />
                  3-4 Attenzione
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-gray-300">
                  <span className="w-2.5 h-2.5 rounded-sm bg-red-500/40 inline-block" />
                  2-3 Critico
                </div>
              </div>
            </div>
          </div>
          {selectedDistricts.length === 0 && (
            <p className="text-red-400 text-xs font-medium text-center mt-1">
              Seleziona un distretto muscolare per visualizzare i dati
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Dropdown semplificato per i distretti check ──────────────────────────────

const RecoveryDistrictDropdown: React.FC<{
  availableDistricts: string[];
  selectedDistricts: string[];
  onSelectionChange: (districts: string[]) => void;
}> = ({ availableDistricts, selectedDistricts, onSelectionChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleDistrict = (district: string) => {
    if (selectedDistricts.includes(district)) {
      onSelectionChange(selectedDistricts.filter((d) => d !== district));
    } else {
      onSelectionChange([...selectedDistricts, district]);
    }
  };

  const getButtonLabel = () => {
    if (selectedDistricts.length === 0) return "Seleziona muscolo...";
    if (selectedDistricts.length <= 2) return selectedDistricts.join(", ");
    return `${selectedDistricts.length} muscoli selezionati`;
  };

  return (
    <div ref={ref} className="relative inline-block w-full max-w-xs">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-sm text-gray-200 hover:bg-gray-600 transition-colors"
      >
        <span className="truncate">{getButtonLabel()}</span>
        <svg
          className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-gray-800 border border-gray-600 rounded-lg shadow-xl max-h-80 overflow-y-auto">
          <button
            onClick={() => onSelectionChange([])}
            className="w-full px-4 py-2 text-xs text-gray-400 hover:bg-gray-700 text-left border-b border-gray-700"
          >
            Reset selezione
          </button>

          {CHECK_DISTRICTS.map((district) => {
            const isAvailable = availableDistricts.includes(district);
            const isSelected = selectedDistricts.includes(district);

            return (
              <button
                key={district}
                onClick={() => isAvailable && toggleDistrict(district)}
                disabled={!isAvailable}
                className={`w-full flex items-center gap-2 px-4 py-2 transition-colors border-b border-gray-700/50 ${
                  isAvailable
                    ? "hover:bg-gray-700 cursor-pointer"
                    : "opacity-30 cursor-not-allowed"
                }`}
              >
                <span
                  className={`w-4 h-4 rounded flex-shrink-0 border flex items-center justify-center text-[10px] ${
                    isSelected
                      ? "bg-blue-500 border-blue-500 text-white"
                      : "border-gray-500"
                  }`}
                >
                  {isSelected ? "✓" : ""}
                </span>
                <span className={`text-sm ${isSelected ? "text-gray-200 font-medium" : isAvailable ? "text-gray-400" : "text-gray-600"}`}>
                  {district}
                </span>
                {!isAvailable && (
                  <span className="text-[10px] text-gray-600 ml-auto">nessun check</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
